import React, { ReactNode, CSSProperties, HTMLAttributes } from 'react';
import { I } from '../icons-ds';

export type TagKind =
  | 'neutral'
  | 'accent'
  | 'inpatient'
  | 'outpatient'
  | 'icu'
  | 'ed'
  | 'exam'
  | 'protocols'
  | 'references'
  | 'success'
  | 'warning'
  | 'danger';

export type TagProps = Omit<HTMLAttributes<HTMLSpanElement>, 'style'> & {
  kind?: TagKind;
  children?: ReactNode;
  removable?: boolean;
  onRemove?: () => void;
  icon?: ReactNode;
  style?: CSSProperties;
};

type Palette = { fg: string; bg: string };

const PALETTES: Record<TagKind, Palette> = {
  neutral: { fg: 'var(--fg-secondary)', bg: 'var(--bg-fill-1)' },
  accent: { fg: 'var(--accent)', bg: 'var(--accent-fill)' },
  inpatient: { fg: 'var(--tag-inpatient-fg)', bg: 'var(--tag-inpatient-bg)' },
  outpatient: { fg: 'var(--tag-outpatient-fg)', bg: 'var(--tag-outpatient-bg)' },
  icu: { fg: 'var(--tag-icu-fg)', bg: 'var(--tag-icu-bg)' },
  ed: { fg: 'var(--tag-ed-fg)', bg: 'var(--tag-ed-bg)' },
  exam: { fg: 'var(--tag-exam-fg)', bg: 'var(--tag-exam-bg)' },
  protocols: { fg: 'var(--tag-protocols-fg)', bg: 'var(--tag-protocols-bg)' },
  references: { fg: 'var(--tag-references-fg)', bg: 'var(--tag-references-bg)' },
  success: { fg: 'var(--success)', bg: 'var(--success-fill)' },
  warning: { fg: 'var(--warning)', bg: 'var(--warning-fill)' },
  danger: { fg: 'var(--danger)', bg: 'var(--danger-fill)' },
};

export function Tag({
  kind = 'neutral',
  children,
  removable,
  onRemove,
  icon,
  style,
  ...rest
}: TagProps) {
  const p = PALETTES[kind] || PALETTES.neutral;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px',
        borderRadius: 'var(--r-pill)',
        background: p.bg, color: p.fg,
        fontSize: 12, fontWeight: 600, letterSpacing: '-0.08px',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
      {removable && (
        <button
          onClick={onRemove}
          style={{
            display: 'inline-flex', marginLeft: 2,
            color: 'currentColor', opacity: 0.7,
            background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
          }}
          aria-label="Remove"
        >
          <I.close size={12} stroke={2} />
        </button>
      )}
    </span>
  );
}

export default Tag;
